import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  adminAuthEndpoints,
  type AuthProviderDto,
  type AuthProviderRoleMappingDto,
  type AuthServerProfileDto,
  type CredentialTypeDto,
  type LocalOidcCredentialRequestDto,
  type OidcCredentialDto,
  type OidcCredentialStatusDto,
  type UnmappedRolePolicyDto,
} from './endpoints';

/**
 * Api layer for the multi-tenant OIDC auth admin screens (PTV2-1412). The wire DTOs are all-optional
 * snake_case; the mappers normalise to non-null camelCase view models. Local query keys keep
 * invalidation self-contained. Write-only secret fields (client id / secret) are never read back;
 * {@code has*} booleans drive the "stored" indicator and the create-vs-keep validation.
 */

export const adminAuthQueryKeys = {
  oidcCredentials: ['admin', 'oidc-credentials'] as const,
  oidcCredentialStatus: (id: string) => ['admin', 'oidc-credentials', 'status', id] as const,
  localOidcAvailability: ['admin', 'oidc-credentials', 'local-availability'] as const,
  authProviders: ['admin', 'auth-providers'] as const,
  authProvider: (id: string) => ['admin', 'auth-providers', id] as const,
};

/* ------------------------------------------------------------------ */
/* OIDC credentials                                                    */
/* ------------------------------------------------------------------ */

export interface OidcCredentialViewModel {
  readonly id: string;
  readonly label: string;
  readonly type: CredentialTypeDto;
  readonly isDefault: boolean;
  readonly issuerUri: string;
  readonly wellKnownUrl: string;
  /** (PTV2-1565) Authorization-server profile; drives whether the gateway sends a SMART `aud`. */
  readonly authServerProfile: AuthServerProfileDto;
  /** (PTV2-1565) Explicit SMART aud (FHIR base URL); blank derives from the well-known URL. */
  readonly fhirAud: string;
  readonly scopes: string;
  readonly hasClientId: boolean;
  readonly hasSecret: boolean;
  /** A synthetic, non-editable built-in entry (e.g. the global bootstrap IdP) — rendered greyed. */
  readonly readOnly: boolean;
}

export function mapOidcCredential(dto: OidcCredentialDto): OidcCredentialViewModel {
  return {
    id: dto.id ?? '',
    label: dto.label ?? '',
    type: dto.type ?? 'CLIENT',
    isDefault: dto.is_default ?? false,
    issuerUri: dto.issuer_uri ?? '',
    wellKnownUrl: dto.well_known_url ?? '',
    authServerProfile: dto.auth_server_profile ?? 'STANDARD_OIDC',
    fhirAud: dto.fhir_aud ?? '',
    scopes: dto.scopes ?? '',
    hasClientId: dto.has_client_id ?? false,
    hasSecret: dto.has_secret ?? false,
    readOnly: dto.read_only ?? false,
  };
}

export interface OidcCredentialInput {
  readonly label: string;
  readonly type: CredentialTypeDto;
  readonly isDefault: boolean;
  readonly issuerUri: string;
  readonly wellKnownUrl: string;
  /** (PTV2-1565) Authorization-server profile; STANDARD_OIDC or SMART_ON_FHIR. */
  readonly authServerProfile: AuthServerProfileDto;
  /** (PTV2-1565) Explicit SMART aud (FHIR base URL); only meaningful for a SMART_ON_FHIR profile. */
  readonly fhirAud: string;
  readonly scopes: string;
  /** Plaintext; empty on update means "keep the stored value". */
  readonly clientId: string;
  /** Plaintext; empty on update means "keep the stored secret". */
  readonly clientSecret: string;
}

export interface UpdateOidcCredentialInput extends OidcCredentialInput {
  readonly id: string;
}

/**
 * Input for a one-click "local application" credential. The backend fills issuer + client id + secret
 * from the cluster's bootstrap-IdP config; the console sends only these operator-facing fields.
 */
export interface LocalOidcCredentialInput {
  readonly label: string;
  readonly type: CredentialTypeDto;
  readonly isDefault: boolean;
  readonly scopes: string;
}

function toLocalCredentialDto(input: LocalOidcCredentialInput): LocalOidcCredentialRequestDto {
  return {
    label: input.label.trim(),
    type: input.type,
    is_default: input.isDefault,
    scopes: blankToUndefined(input.scopes) ?? null,
  };
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toCredentialDto(input: OidcCredentialInput, id?: string): OidcCredentialDto {
  const dto: OidcCredentialDto = {
    ...(id !== undefined ? { id } : {}),
    label: input.label.trim(),
    type: input.type,
    is_default: input.isDefault,
    issuer_uri: input.issuerUri.trim(),
    well_known_url: blankToUndefined(input.wellKnownUrl) ?? null,
    auth_server_profile: input.authServerProfile,
    // Only a SMART_ON_FHIR credential carries an aud; never persist a stale one on a plain OIDC profile.
    fhir_aud:
      input.authServerProfile === 'SMART_ON_FHIR' ? (blankToUndefined(input.fhirAud) ?? null) : null,
    scopes: blankToUndefined(input.scopes) ?? null,
  };
  const clientId = blankToUndefined(input.clientId);
  if (clientId !== undefined) {
    dto.client_id = clientId;
  }
  const clientSecret = blankToUndefined(input.clientSecret);
  if (clientSecret !== undefined) {
    dto.client_secret = clientSecret;
  }
  return dto;
}

export function useOidcCredentials(): UseQueryResult<OidcCredentialViewModel[]> {
  return useQuery<OidcCredentialViewModel[]>({
    queryKey: adminAuthQueryKeys.oidcCredentials,
    queryFn: async ({ signal }) => {
      const list = await adminAuthEndpoints.getOidcCredentials({ signal });
      return list.map(mapOidcCredential);
    },
  });
}

/** Whether the "Add local application" button applies in this environment (bootstrap IdP present). */
export function useLocalOidcAvailability(): UseQueryResult<boolean> {
  return useQuery<boolean>({
    queryKey: adminAuthQueryKeys.localOidcAvailability,
    queryFn: async ({ signal }) => {
      const dto = await adminAuthEndpoints.getLocalOidcAvailability({ signal });
      return dto.available ?? false;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Live connectivity probe (OIDC discovery + JWKS) for one credential, queried on mount. */
export function useOidcCredentialStatus(id: string): UseQueryResult<OidcCredentialStatusDto> {
  return useQuery<OidcCredentialStatusDto>({
    queryKey: adminAuthQueryKeys.oidcCredentialStatus(id),
    queryFn: ({ signal }) => adminAuthEndpoints.getOidcCredentialStatus(id, { signal }),
    staleTime: 60 * 1000,
    retry: false,
  });
}

export function useOidcCredentialMutations(): {
  readonly create: UseMutationResult<OidcCredentialViewModel, Error, OidcCredentialInput>;
  readonly createLocal: UseMutationResult<OidcCredentialViewModel, Error, LocalOidcCredentialInput>;
  readonly update: UseMutationResult<OidcCredentialViewModel, Error, UpdateOidcCredentialInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: adminAuthQueryKeys.oidcCredentials })
      .catch(() => undefined);
    // A credential's type/default drives the provider form (providers inherit the type), so refresh both.
    queryClient
      .invalidateQueries({ queryKey: adminAuthQueryKeys.authProviders })
      .catch(() => undefined);
  };

  const create = useMutation<OidcCredentialViewModel, Error, OidcCredentialInput>({
    mutationFn: async (input) =>
      mapOidcCredential(await adminAuthEndpoints.createOidcCredential(toCredentialDto(input))),
    onSettled: invalidate,
  });

  const createLocal = useMutation<OidcCredentialViewModel, Error, LocalOidcCredentialInput>({
    mutationFn: async (input) =>
      mapOidcCredential(await adminAuthEndpoints.createLocalOidcCredential(toLocalCredentialDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<OidcCredentialViewModel, Error, UpdateOidcCredentialInput>({
    mutationFn: async ({ id, ...rest }) =>
      mapOidcCredential(
        await adminAuthEndpoints.updateOidcCredential(id, toCredentialDto(rest, id)),
      ),
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    mutationFn: (id) => adminAuthEndpoints.deleteOidcCredential(id),
    onSettled: invalidate,
  });

  return { create, createLocal, update, remove };
}

/* ------------------------------------------------------------------ */
/* Auth providers                                                      */
/* ------------------------------------------------------------------ */

export interface AuthProviderRoleMappingViewModel {
  readonly externalRole: string;
  readonly internalRole: string;
}

export interface AuthProviderViewModel {
  readonly id: string;
  readonly host: string;
  readonly tenantUuid: string;
  /** Derived, read-only: inherited from the bound credential (PTV2-1383). */
  readonly type: CredentialTypeDto | null;
  readonly credentialId: string;
  readonly tenantIdClaimKey: string;
  /** (PTV2-1520) External IdP tenant/org id matched against the tenant_id claim; '' = match internal UUID. */
  readonly externalTenantId: string;
  /** (PTV2-1520) When false, the tenant-id claim assertion is skipped. Default true. */
  readonly tenantClaimCheckEnabled: boolean;
  readonly userIdClaimKey: string;
  readonly rolesClaimKey: string;
  readonly rolesFromClaimEnabled: boolean;
  readonly unmappedRolePolicy: UnmappedRolePolicyDto;
  readonly userinfoEnabled: boolean;
  /** (PTV2-1523) Claim mapped to the user's full display name; wins over joined first/last. */
  readonly claimName: string;
  readonly claimFirstName: string;
  readonly claimLastName: string;
  readonly claimEmail: string;
  readonly jitProvisioningEnabled: boolean;
  /** (PTV2-1425) JIT-created users are ACTIVE immediately instead of PENDING_ACTIVATION. */
  readonly newUserAccountsAreActive: boolean;
  readonly enabled: boolean;
  /** A synthetic, non-editable built-in binding (e.g. the global bootstrap IdP) — rendered greyed. */
  readonly readOnly: boolean;
  readonly roleMappings: readonly AuthProviderRoleMappingViewModel[];
  readonly defaultRoles: readonly string[];
}

/**
 * (PTV2-1520) Whether a credential's type is valid for a binding on the given plane, mirroring the
 * backend type↔plane invariant: Global (no tenant) admits only CLUSTER_OPERATOR; a tenant admits only
 * CLIENT / TENANT_OPERATOR. A null type (nothing selected) matches nothing.
 */
export function credentialMatchesPlane(type: CredentialTypeDto | null, isGlobal: boolean): boolean {
  if (type === null) return false;
  return isGlobal ? type === 'CLUSTER_OPERATOR' : type === 'CLIENT' || type === 'TENANT_OPERATOR';
}

export function mapAuthProvider(dto: AuthProviderDto): AuthProviderViewModel {
  return {
    id: dto.id ?? '',
    host: dto.host ?? '',
    tenantUuid: dto.tenant_uuid ?? '',
    type: dto.type ?? null,
    credentialId: dto.credential_id ?? '',
    tenantIdClaimKey: dto.tenant_id_claim_key ?? '',
    externalTenantId: dto.external_tenant_id ?? '',
    tenantClaimCheckEnabled: dto.tenant_claim_check_enabled ?? true,
    userIdClaimKey: dto.user_id_claim_key ?? '',
    rolesClaimKey: dto.roles_claim_key ?? '',
    rolesFromClaimEnabled: dto.roles_from_claim_enabled ?? false,
    unmappedRolePolicy: dto.unmapped_role_policy ?? 'FALLBACK_TO_DEFAULTS',
    userinfoEnabled: dto.userinfo_enabled ?? false,
    claimName: dto.claim_name ?? '',
    claimFirstName: dto.claim_first_name ?? '',
    claimLastName: dto.claim_last_name ?? '',
    claimEmail: dto.claim_email ?? '',
    jitProvisioningEnabled: dto.jit_provisioning_enabled ?? false,
    newUserAccountsAreActive: dto.new_user_accounts_are_active ?? false,
    enabled: dto.enabled ?? true,
    readOnly: dto.read_only ?? false,
    roleMappings: (dto.role_mappings ?? []).map((m) => ({
      externalRole: m.external_role,
      internalRole: m.internal_role,
    })),
    defaultRoles: dto.default_roles ?? [],
  };
}

export interface AuthProviderInput {
  readonly host: string;
  readonly tenantUuid: string;
  readonly credentialId: string;
  readonly tenantIdClaimKey: string;
  /** (PTV2-1520) External IdP tenant/org id; '' => null (match internal UUID). */
  readonly externalTenantId: string;
  /** (PTV2-1520) When false, skip the tenant-id claim assertion. */
  readonly tenantClaimCheckEnabled: boolean;
  readonly userIdClaimKey: string;
  readonly rolesClaimKey: string;
  readonly rolesFromClaimEnabled: boolean;
  readonly unmappedRolePolicy: UnmappedRolePolicyDto;
  readonly userinfoEnabled: boolean;
  readonly claimName: string;
  readonly claimFirstName: string;
  readonly claimLastName: string;
  readonly claimEmail: string;
  readonly jitProvisioningEnabled: boolean;
  readonly newUserAccountsAreActive: boolean;
  readonly enabled: boolean;
  readonly roleMappings: readonly AuthProviderRoleMappingViewModel[];
  readonly defaultRoles: readonly string[];
}

export interface UpdateAuthProviderInput extends AuthProviderInput {
  readonly id: string;
}

function toProviderDto(input: AuthProviderInput, id?: string): AuthProviderDto {
  const roleMappings: AuthProviderRoleMappingDto[] = input.roleMappings
    .filter((m) => m.externalRole.trim().length > 0 && m.internalRole.trim().length > 0)
    .map((m) => ({ external_role: m.externalRole.trim(), internal_role: m.internalRole.trim() }));
  const defaultRoles = input.defaultRoles.map((r) => r.trim()).filter((r) => r.length > 0);

  return {
    ...(id !== undefined ? { id } : {}),
    host: input.host.trim(),
    tenant_uuid: blankToUndefined(input.tenantUuid) ?? null,
    // PTV2-1383: no type on write — the provider inherits it from the bound credential.
    credential_id: input.credentialId,
    tenant_id_claim_key: blankToUndefined(input.tenantIdClaimKey) ?? null,
    // PTV2-1520: blank external id => null (match internal UUID); the claim-check toggle is sent as-is.
    external_tenant_id: blankToUndefined(input.externalTenantId) ?? null,
    tenant_claim_check_enabled: input.tenantClaimCheckEnabled,
    user_id_claim_key: input.userIdClaimKey.trim(),
    roles_claim_key: blankToUndefined(input.rolesClaimKey) ?? null,
    roles_from_claim_enabled: input.rolesFromClaimEnabled,
    unmapped_role_policy: input.unmappedRolePolicy,
    userinfo_enabled: input.userinfoEnabled,
    claim_name: blankToUndefined(input.claimName) ?? null,
    claim_first_name: blankToUndefined(input.claimFirstName) ?? null,
    claim_last_name: blankToUndefined(input.claimLastName) ?? null,
    claim_email: blankToUndefined(input.claimEmail) ?? null,
    jit_provisioning_enabled: input.jitProvisioningEnabled,
    new_user_accounts_are_active: input.newUserAccountsAreActive,
    enabled: input.enabled,
    role_mappings: roleMappings,
    default_roles: defaultRoles,
  };
}

export function useAuthProviders(): UseQueryResult<AuthProviderViewModel[]> {
  return useQuery<AuthProviderViewModel[]>({
    queryKey: adminAuthQueryKeys.authProviders,
    queryFn: async ({ signal }) => {
      const list = await adminAuthEndpoints.getAuthProviders({ signal });
      return list.map(mapAuthProvider);
    },
  });
}

/** A single auth-provider binding by id. Drives the detail page (PTV2-1383). */
export function useAuthProvider(id: string): UseQueryResult<AuthProviderViewModel> {
  return useQuery<AuthProviderViewModel>({
    queryKey: adminAuthQueryKeys.authProvider(id),
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      mapAuthProvider(await adminAuthEndpoints.getAuthProvider(id, { signal })),
  });
}

export function useAuthProviderMutations(): {
  readonly create: UseMutationResult<AuthProviderViewModel, Error, AuthProviderInput>;
  readonly update: UseMutationResult<AuthProviderViewModel, Error, UpdateAuthProviderInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: adminAuthQueryKeys.authProviders })
      .catch(() => undefined);
  };

  const create = useMutation<AuthProviderViewModel, Error, AuthProviderInput>({
    mutationFn: async (input) =>
      mapAuthProvider(await adminAuthEndpoints.createAuthProvider(toProviderDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<AuthProviderViewModel, Error, UpdateAuthProviderInput>({
    mutationFn: async ({ id, ...rest }) =>
      mapAuthProvider(await adminAuthEndpoints.updateAuthProvider(id, toProviderDto(rest, id))),
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    mutationFn: (id) => adminAuthEndpoints.deleteAuthProvider(id),
    onSettled: invalidate,
  });

  return { create, update, remove };
}
